import type { DataSource } from './DataSource'
import type { FieldDef } from '../domain/types'

// P-1/P-2/P-3: cache de definição de campos de form (getTransitionFields /
// getJourneyFields). Campos mudam raramente (config) — TTL curto cobre
// staleness. Single-flight por chave: hover-warm e modal-open compartilham
// a MESMA promise, então o mock/ds vê uma única chamada por ticket+trigger.

const TTL_MS = 60_000

interface Entry {
  fields: FieldDef[]
  at: number
  inflight?: Promise<FieldDef[]>
}

const entries = new Map<string, Entry>()

const settled = (e: Entry): boolean => e.at > 0 && Date.now() - e.at < TTL_MS

async function readThrough(
  fetcher: () => Promise<FieldDef[]>,
  key: string,
  opts?: { swr?: boolean },
): Promise<FieldDef[]> {
  const e = entries.get(key)
  if (e?.inflight) {return e.inflight}
  if (e && settled(e)) {return e.fields}
  if (opts?.swr && e && e.at > 0) {
    // stale-while-revalidate: devolve o cache já; refetch em background.
    fetcher()
      .then((f) => { entries.set(key, { fields: f, at: Date.now() }) })
      .catch(() => {})
    return e.fields
  }
  const inflight = fetcher()
    .then((f) => { entries.set(key, { fields: f, at: Date.now() }); return f })
    .catch((err) => { entries.delete(key); throw err })
  entries.set(key, { fields: [], at: 0, inflight })
  return inflight
}

const tKey = (ticketId: number, trigger: string) => `t:${ticketId}:${trigger}`
const cKey = (templateId: number | null, customerId: number | null, trigger?: string) =>
  `c:${templateId}:${customerId}:${trigger ?? ''}`

/** Prefetch discreto (hover/focus/click). Nunca rejeita. */
export function warmTransitionFields(ds: DataSource, ticketId: number, trigger: string): void {
  if (ticketId <= 0) {return}
  void readThrough(() => ds.getTransitionFields(ticketId, trigger), tKey(ticketId, trigger)).catch(() => {})
}

/** Read-through com cache/TTL/single-flight. Uso no on-click (requestTransition, modais). */
export function cachedTransitionFields(ds: DataSource, ticketId: number, trigger: string): Promise<FieldDef[]> {
  return readThrough(() => ds.getTransitionFields(ticketId, trigger), tKey(ticketId, trigger))
}

/** getJourneyFields com cache + stale-while-revalidate (P-3). */
export function cachedCreationFields(
  ds: DataSource,
  templateId: number | null,
  customerId: number | null,
  trigger?: string,
): Promise<FieldDef[]> {
  return readThrough(
    () => ds.getJourneyFields(templateId, customerId, trigger),
    cKey(templateId, customerId, trigger),
    { swr: true },
  )
}

/** Invalida tudo (pós-ação — chamado no reload do App). */
export function invalidatePrefetch(): void {
  entries.clear()
}
